// Section 20: the eight steady-state conditions, tested against the trailing 4 seasons and the
// trailing 3 years. Pure; the steady phase applies the counter, steadyOf() reports on demand.
import { seasonalSlope, slope, sum } from '../../core/math';
import { isChild, peopleSorted, settlementTrust } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type {
  ReadContext,
  SeasonReport,
  SteadyCondition,
  SteadyConditionResult,
} from '../../core/types';

const DEPRIVATION = new Set(['starvation', 'exposure', 'dehydration']);

function recentReports(ctx: ReadContext, n: number): SeasonReport[] {
  const all =
    ctx.state.currentReport === null
      ? ctx.state.history
      : [...ctx.state.history, ctx.state.currentReport];
  return all.slice(-n);
}

function lastN(series: readonly number[], n: number): number[] {
  return series.slice(Math.max(0, series.length - n));
}

export function steadyConditions(ctx: ReadContext): Record<SteadyCondition, SteadyConditionResult> {
  const s = ctx.cfg.steady;
  const state = ctx.state;
  const recent = recentReports(ctx, s.lookbackSeasons);
  const people = peopleSorted(state);
  const pop = people.length;

  const deprivation = sum(
    recent.map(
      (r) => r.entries.filter((e) => e.kind === 'death' && DEPRIVATION.has(e.cause)).length,
    ),
  );

  let personSeasons = 0;
  let met = 0;
  const t = ctx.cfg.needs.metThreshold;
  for (const r of recent) {
    for (const n of Object.values(r.needs)) {
      personSeasons++;
      if (n.food >= t && n.water >= t && n.shelter >= t && n.warmth >= t) met++;
    }
  }
  const coverage = personSeasons === 0 ? 0 : met / personSeasons;

  const popSeries = state.series.population;
  const then = popSeries[Math.max(0, popSeries.length - 1 - s.populationBandSeasons)] ?? pop;
  const popChange = then > 0 ? Math.abs(pop - then) / then : 1;

  const births = sum(
    lastN(state.series.births, s.birthsLookbackYears * ctx.cfg.people.seasonsPerYear),
  );
  const under15 = pop === 0 ? 0 : people.filter((p) => isChild(ctx.cfg, p)).length / pop;

  let worstSlope = Number.POSITIVE_INFINITY;
  for (const r of s.stockTrendResources) {
    const values = lastN(
      state.series[r as 'food' | 'water' | 'wood' | 'tools'],
      s.stockTrendSeasons,
    );
    const m = values.length === 0 ? 0 : sum(values) / values.length;
    // ADR-0018: fitted with a term per season of the year, so the harvest and Winter swing that
    // repeats every year is not a trend (the window is whole years, ending at a Winter); and
    // measured against the larger of the mean and a share of the store, so a stock held low
    // (tools kept just ahead of wear, water carried to demand) is not failed by ordinary swings.
    const perSeason = s.stockTrendBySeason
      ? seasonalSlope(values, ctx.cfg.people.seasonsPerYear)
      : slope(values);
    const perYear = perSeason * ctx.cfg.people.seasonsPerYear;
    const scale = Math.max(m, s.stockTrendCapacityShare * state.stocks[r].capacity);
    worstSlope = Math.min(worstSlope, scale > 0 ? perYear / scale : 0);
  }
  if (!Number.isFinite(worstSlope)) worstSlope = 0;

  const buildings = sortedValues(state.buildings);
  const minCondition =
    buildings.length === 0 ? 100 : Math.min(...buildings.map((b) => b.condition));

  const trust = settlementTrust(state);
  const window = ctx.season.turn - s.lookbackSeasons;
  const killing =
    state.safety.lastUndetectedKillingTurn !== null &&
    state.safety.lastUndetectedKillingTurn > window;
  const refusal =
    state.counters.lastRefusalTurn !== null && state.counters.lastRefusalTurn > window;

  const covered = s.knowledgeSkills.filter((k) =>
    people.some((p) => (p.skills[k] ?? 0) >= s.knowledgeMinSkill),
  ).length;

  return {
    'no-deprivation-deaths': { pass: deprivation === 0, value: deprivation, threshold: 0 },
    'needs-met': { pass: coverage >= s.needsCoverage, value: coverage, threshold: s.needsCoverage },
    'population-stable': {
      pass: popChange <= s.populationBand && pop >= s.minPopulation,
      value: popChange,
      threshold: s.populationBand,
    },
    replacement: {
      pass: births >= s.minBirths && under15 >= s.minUnder15Share,
      value: births,
      threshold: s.minBirths,
    },
    'stocks-stable': {
      pass: worstSlope >= s.stockSlopeMinPerYear,
      value: worstSlope,
      threshold: s.stockSlopeMinPerYear,
    },
    'buildings-maintained': {
      pass: minCondition >= s.minBuildingCondition,
      value: minCondition,
      threshold: s.minBuildingCondition,
    },
    'social-floor': {
      pass: trust >= s.minTrust && !killing && !refusal,
      value: trust,
      threshold: s.minTrust,
    },
    knowledge: {
      pass: covered === s.knowledgeSkills.length,
      value: covered,
      threshold: s.knowledgeSkills.length,
    },
  };
}
